import "./order-card-details.scss";

import classNames from "classnames";
import { motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import {
	FunctionComponent,
	MouseEvent,
	RefObject,
	useEffect,
	useRef,
	useState,
} from "react";

import { PackagesModal } from "@client/components/packages-modal";
import { getTotalNumberOfProducts } from "@client/components/reusable/hooks/useGetTotalNumberOfProducts";
import ScheduledDate from "@client/components/reusable/scheduled-date/scheduled-date";
import { useRootContext } from "@client/context/RootContext";
import { getFullDateFormat } from "@client/helpers/getFullDateFormat";
import { TDateISO } from "@client/interfaces";
import { Order } from "@client/stores";
import { LABELS } from "@client/utils/labels";

import { ProductDetails } from "./product-details";

interface OrderCardDetailsProps {
	order: Order;
	orderCardRef?: RefObject<HTMLDivElement>;

	toggleShowModal?: (e: MouseEvent) => any;

	toggleShowCancelledOrderModal?: (e: MouseEvent) => void;
	closeOrderCardModal?: () => any;
	isPickingTab?: boolean;
	isReadyToPickModal?: boolean;
	isCancelledOrder?: boolean;
	isOrdersHistory?: boolean;
	isOrderCardModal?: boolean;
}

export const OrderCardDetails: FunctionComponent<OrderCardDetailsProps> =
	observer(
		({
			order,
			orderCardRef,

			toggleShowModal,

			toggleShowCancelledOrderModal,
			closeOrderCardModal,
			isPickingTab,
			isReadyToPickModal,
			isCancelledOrder,
			isOrdersHistory,
			isOrderCardModal,
		}) => {
			const {
				modalStore,
				vendorStore,
				printStore: { printShippingLabel },
				vendorStore: {
					orderOperations: { acceptOrder, collectedOrder, readyForDelivery },
				},
			} = useRootContext();

			const [dateError, setDateError] = useState(false);
			const [scheduledDate, setScheduledDate] = useState<TDateISO | undefined>(
				order.dates.scheduled_date
			);

			function handleScheduledDateChange(date: TDateISO | undefined) {
				setScheduledDate(date);
				vendorStore.setScheduledDate(order.id, date);
			}

			useEffect(() => {
				if (scheduledDate) {
					setDateError(false);
				}
			}, [scheduledDate]);
			useEffect(() => {
				setScheduledDate(order.dates.scheduled_date);
			}, [order.dates.scheduled_date]);

			const totalProducts = getTotalNumberOfProducts(order);
			const [rejectedProducts, setRejectedProducts] = useState<string[]>([]);

			const orderContainerExpandedRef = useRef<HTMLDivElement>(null);
			const products = order.products;
			const itemsPerPackageThreshold = Number(
				order.shipping_details.user_details?.items_per_package_treshold
			);

			function addRejectedProduct(id: string) {
				setRejectedProducts([...rejectedProducts, id]);
			}

			function removeRejectedProduct(id: string) {
				const indexOfId = rejectedProducts.findIndex(
					(rejectedProductId) => rejectedProductId === id
				);
				const rejectedProductsArr = [...rejectedProducts];
				if (indexOfId >= 0) {
					rejectedProductsArr.splice(indexOfId, 1);
					setRejectedProducts(rejectedProductsArr);
				}
			}

			function getRemainingItemsLength() {
				const rejectProductsLength =
					rejectedProducts?.length >= 0 ? rejectedProducts.length : 0;
				if (rejectedProducts?.length >= 0 && totalProducts) {
					return totalProducts - rejectProductsLength;
				}
				return 0;
			}

			function handleCollectedBtn(e: MouseEvent) {
				e.preventDefault();
				e.stopPropagation();

				vendorStore.setActualDate(order.id);
				collectedOrder?.(order.id);
				modalStore.removeModal("order-ready-modal-" + order.id);
			}

			function handlePrintBtn(e: MouseEvent) {
				e.preventDefault();
				e.stopPropagation();

				const reference = order.order_meta?.reference || String(order.id);
				printShippingLabel(reference);
			}

			function openPackagesModal(e: MouseEvent) {
				e.preventDefault();
				e.stopPropagation();

				modalStore.addModal({
					id: "packages-modal",
					showCloseBtn: true,
					content: (
						<PackagesModal
							orderId={order.id}
							rejectedProducts={rejectedProducts}
							closeOrderCardModal={closeOrderCardModal}
						/>
					),
				});
			}

			useEffect(() => {
				const rejectedProductsArr = [];
				// check and add the products which are rejected to rejectedProductsArr
				for (let i = 0; i < products.length; i++) {
					const quantity = products[i]?.order_details?.qtyCanceled;
					if (quantity) {
						const quantityArr = [...Array(quantity).keys()];
						for (let j = 0; j < quantityArr.length; j++) {
							rejectedProductsArr.push(
								products[i]?.id + "-" + String(quantityArr[j])
							);
						}
					}
				}
				setRejectedProducts(rejectedProductsArr);
			}, [order.products]);

			return (
				<motion.div
					className={classNames("products-details-wrapper")}
					ref={orderContainerExpandedRef}
				>
					<div className="border-line" />

					{(isPickingTab ||
						(isReadyToPickModal && order.dates.scheduled_date)) &&
						vendorStore.vendorDetails?.delivery_dates_enabled && (
							<ScheduledDate
								error={dateError}
								isReadyTab={isReadyToPickModal}
								isPopup
								scheduledDate={scheduledDate}
								setScheduledDate={handleScheduledDateChange}
							/>
						)}

					<div className="product-container">
						{isOrderCardModal && (
							<div className="order-details-date">
								<span className="created-text">Created: </span>{" "}
								{getFullDateFormat(
									order.order_meta.createdAt || order.dates.received.received_at
								)}
							</div>
						)}
						{order.products.length > 0 &&
							order.products.map((product) => {
								const quantity = product.order_details?.quantity;
								if (quantity > 1) {
									const quantityArr = [...Array(quantity).keys()];
									return (
										<div key={"products-wrapper-" + product.sku}>
											{quantityArr.map((item) => {
												return (
													<ProductDetails
														key={`${product.sku}-${item}`}
														product={product}
														order={order}
														idx={item}
														rejectedProducts={rejectedProducts}
														addRejectedProduct={addRejectedProduct}
														removeRejectedProduct={removeRejectedProduct}
													/>
												);
											})}
										</div>
									);
								} else
									return (
										<ProductDetails
											key={product.sku}
											product={product}
											order={order}
											idx={0}
											rejectedProducts={rejectedProducts}
											addRejectedProduct={addRejectedProduct}
											removeRejectedProduct={removeRejectedProduct}
										/>
									);
							})}
					</div>

					<div className="border-line" />

					<div className="buttons-section">
						{isReadyToPickModal || isOrdersHistory ? (
							<>
								<button className="print-btn" onClick={handlePrintBtn}>
									Print again
								</button>
								<button
									className="collected-by-courier-btn"
									onClick={
										isOrdersHistory ? closeOrderCardModal : handleCollectedBtn
									}
								>
									{isOrdersHistory ? "Cancel" : LABELS.collectedByCourier}
								</button>
							</>
						) : isCancelledOrder ? (
							<button
								className="close-btn"
								onClick={toggleShowCancelledOrderModal}
							>
								Close
							</button>
						) : (
							<>
								<button
									className={classNames("accept-btn", {
										["no-items"]: totalProducts === rejectedProducts?.length,
									})}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();

										if (
											isPickingTab &&
											vendorStore.vendorDetails?.delivery_dates_enabled &&
											!scheduledDate
										) {
											setDateError(true);
											return;
										}

										if (
											getRemainingItemsLength() >= itemsPerPackageThreshold &&
											isPickingTab
										) {
											openPackagesModal(e);
											return;
										}

										closeOrderCardModal && closeOrderCardModal();

										isPickingTab
											? readyForDelivery?.(order.id, rejectedProducts)
											: acceptOrder?.(order.id, rejectedProducts);
									}}
								>
									{totalProducts === rejectedProducts?.length
										? `0 remaining items`
										: rejectedProducts?.length
										? `Accept ${getRemainingItemsLength()} remaining items`
										: isPickingTab
										? "Ready to delivery"
										: "Accept order"}
								</button>
								<button className="reject-btn" onClick={toggleShowModal}>
									Reject
								</button>
							</>
						)}
					</div>
				</motion.div>
			);
		}
	);
